import dataLayer from 'assets/core/js/module/dataLayer'
import type { Calendar } from '@ui/Search/Calendar/component'
import type { SearchAutocomplete } from '@ui/Search/Form/Fields/Autocomplete/component'
import type { TravelerSelector } from '@ui/TravelerSelector/component'

export enum EventType {
  BRAND = 'brand',
  GEO = 'geo',
  MANUAL = 'manual',
  PRODUCT = 'product',
  SUGGESTION = 'suggested_search',
  UNKNOWN = '',
}

export interface Event {
  type: EventType
  term: string
  user_term: string
  suggested_date: boolean
  checkin: Date | null
  flexible: boolean
  guests: string
  night: string | number
}

export const push = (event: Event): void => {
  const dataLayerEvent = {
    event: dataLayer.events.SEARCH,
    search_type: event.type.toString(),
    search_term: event.term,
    search_user_term: event.user_term,
    suggested_date: event.suggested_date,
    search_checkin_date: formatDate(event.checkin),
    search_flexibility: event.flexible,
    search_guests: event.guests,
    search_night: event.night,
  }

  dataLayer.pushInLayer(window.coreGtmId, dataLayerEvent)
}

export default function (
  autocompleteInstance: SearchAutocomplete | undefined,
  calendarInstance: Calendar | undefined,
  travelerSelectorInstance: TravelerSelector | undefined,
  capacityInstance: HTMLSelectElement | undefined
): void {
  const searchFormSubmitButton = document.querySelector('#search-offers button[type="submit"]') as HTMLButtonElement
  if (!searchFormSubmitButton) {
    return
  }

  // Check if it's a last minute search.
  const lastMinuteForm: HTMLFormElement | null = document.querySelector('#search-offers[name="last_minute_search"]')

  searchFormSubmitButton.addEventListener('click', () => {
    let type = EventType.UNKNOWN
    let term = ''
    let userTerm = ''
    let suggestedDate = false
    let checkin: Date | null = null
    let flexible = false
    let night: string | number = ''
    let guests = ''

    // Handle type and term data.
    if (autocompleteInstance) {
      type = EventType.MANUAL
      term = userTerm = autocompleteInstance.searchValue
      const selection = autocompleteInstance.selection
      if (selection) {
        term = selection.name
        type = mapAutocompleteSelectionToEventType(selection.category)
      }
    }

    // Handle checkin, night, flexible and suggested date data.
    if (calendarInstance) {
      suggestedDate = calendarInstance.isSuggestedDate()
      const checkinDateString = calendarInstance.getDate()
      checkin = checkinDateString ? new Date(checkinDateString) : null
      flexible = calendarInstance.getFlexibleDate()
      const nightsValue = calendarInstance.getNights()
      night = nightsValue ?? ''
    }
    if (lastMinuteForm) {
      const checkinDateString = document
        .querySelector<HTMLElement>('.departure-date-groups .active [data-date], .departure-date-groups .departure__tab--active [data-path]')
        ?.getAttribute('data-date')
      checkin = checkinDateString ? new Date(checkinDateString) : null
      const nightValue = lastMinuteForm.querySelector<HTMLInputElement>('[name="last_minute_search[night]"]')?.value
      night = nightValue ?? ''
    }

    // Handle guests data.
    if (travelerSelectorInstance) {
      guests = travelerSelectorInstance.getTotalPersons().toString()
    }
    if (capacityInstance) {
      guests = capacityInstance.value
    }

    const dataLayerEvent: Event = {
      type: type,
      term: term,
      user_term: userTerm,
      suggested_date: suggestedDate,
      checkin: checkin,
      flexible: flexible,
      guests: guests,
      night: night,
    }

    push(dataLayerEvent)
  })
}

const mapAutocompleteSelectionToEventType = (category: string): EventType => {
  switch (category) {
    case 'product':
      return EventType.PRODUCT
    case 'geo':
      return EventType.GEO
    default:
      return EventType.UNKNOWN
  }
}

/**
 * Format the date to the dd/mm/yyyy format.
 */
const formatDate = (date: Date | null): string => {
  if (!date) {
    return ''
  }

  return Intl.DateTimeFormat('fr-FR').format(date)
}
